import React, { useState } from "react";
import axios from "axios";
import { X, Send, Loader2, CheckCircle2, Rocket } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";

const models = ["Elite Partner", "Pro Partner", "Prime Pro Partner", "Not sure yet"];

export default function ApplyModal({ open, onClose, defaultModel = "" }) {
  const { toast } = useToast();
  const empty = { name: "", mobile: "", email: "", city: "", qualification: "", occupation: "", age: "", model: defaultModel };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm((f) => ({ ...empty, model: defaultModel }));
      setDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultModel]);

  if (!open) return null;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.email || !form.city) {
      return toast({ title: "Please fill the required fields", description: "Name, mobile, email and city are required." });
    }
    setLoading(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      await axios.post(`${API}/partner-application`, form);
      setDone(true);
      toast({ title: "Application sent", description: "Our team will reach out to you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4 bg-[#1a0b26]/85 backdrop-blur-sm animate-fade-up overflow-y-auto" onClick={onClose}>
      <div className="relative w-full max-w-xl my-8 rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-violet-100">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-xl cta-gradient text-white"><Rocket className="h-5 w-5" /></span>
            <p className="text-[18px] font-bold text-slate-900">Apply to start your business</p>
          </div>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-teal-500" />
            <h3 className="mt-3 text-[22px] font-bold text-slate-900">Thank you, {form.name.split(" ")[0]}!</h3>
            <p className="mt-2 text-[16px] text-slate-600">Your application has been received. Our team will contact you shortly to help you get started.</p>
            <Button onClick={onClose} className="mt-6 cta-gradient text-white rounded-full h-12 px-6 text-[16px] font-semibold">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[16px]">Full name *</Label>
                <Input value={form.name} onChange={update("name")} placeholder="Your name" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">Mobile *</Label>
                <Input value={form.mobile} onChange={update("mobile")} placeholder="Your mobile number" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">Email *</Label>
                <Input type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">City *</Label>
                <Input value={form.city} onChange={update("city")} placeholder="Your city" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">Qualification</Label>
                <Input value={form.qualification} onChange={update("qualification")} placeholder="Highest qualification" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">Current occupation</Label>
                <Input value={form.occupation} onChange={update("occupation")} placeholder="What you do now" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">Age</Label>
                <Input value={form.age} onChange={update("age")} placeholder="Your age" className="h-12 text-[16px] rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label className="text-[16px]">Interested plan</Label>
                <Select value={form.model} onValueChange={update("model")}>
                  <SelectTrigger className="h-12 text-[16px] rounded-xl" data-testid="apply-plan-trigger"><SelectValue placeholder="Choose a plan" /></SelectTrigger>
                  <SelectContent className="z-[200]">
                    {models.map((m) => <SelectItem key={m} value={m} className="text-[16px]">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full cta-gradient text-white rounded-full h-13 h-14 text-[17px] font-semibold">
              {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</> : <>Submit Application <Send className="ml-2 h-5 w-5" /></>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
